// src/stores/user.ts
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { User, KycStatus, Money, PayoutInstruction, PaymentGateway, Purchase, PayoutBalance } from '@/types'
import { authService } from '@/services/authService'
import type { TwoFactorChallenge } from '@/services/authService'
import { userService } from '@/services/userService'
import { useI18n } from '@/composables/useI18n'
import { useSavedSearches } from '@/composables/useSavedSearches'

// Generic avatar placeholder (inline SVG) used when the profile has no image.
const PLACEHOLDER_AVATAR =
  'data:image/svg+xml;utf8,' +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="#e8edf2"/><circle cx="50" cy="38" r="18" fill="#9aa7b4"/><path d="M14 88c4-20 19-30 36-30s32 10 36 30z" fill="#9aa7b4"/></svg>`
  )

export const useUserStore = defineStore('user', () => {
  const { t } = useI18n()

  // State — starts UNAUTHENTICATED. There is no demo-user fallback: every
  // auth state must come from a real backend response.
  const user = ref<User | null>(null)
  const isAuthenticated = ref(false)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  
  // KYC
  const kycStatus = ref<KycStatus>('not_started')
  const kycDocuments = ref<{
    idFront?: string
    idBack?: string
    selfie?: string
    proofOfAddress?: string
  }>({})
  
  // Payments
  const savedCards = ref<Array<{
    id: string
    brand: string
    last4: string
    expiryMonth: number
    expiryYear: number
    gateway: PaymentGateway
    isDefault: boolean
  }>>([])
  
  // Won lots and where each one's escrow settlement stands (GET /api/user/purchases).
  const purchases = ref<Purchase[]>([])
  // What the seller can withdraw right now — comes with GET /api/user/payouts.
  const payoutBalance = ref<PayoutBalance | null>(null)
  
  // Payouts
  const payoutMethods = ref<Array<{
    id: string
    bankCode: string
    bankName: string
    accountNumber: string
    accountHolderName: string
    isVerified: boolean
    isDefault: boolean
  }>>([])
  
  const payoutHistory = ref<PayoutInstruction[]>([])
  const payoutHistoryMeta = ref({ currentPage: 1, lastPage: 1, perPage: 20, total: 0 })
  
  // Bidding
  const activeBids = ref<Array<{
    auctionId: string
    auctionTitle: string
    amount: Money
    status: string
    placedAt: string
  }>>([])
  
  const wonAuctions = ref<Array<{
    id: string
    title: string
    amount: Money
    wonAt: string
    status: string
  }>>([])

  // Getters
  const fullName = computed(() => user.value?.fullName || '')
  const avatar = computed(() => user.value?.avatar || PLACEHOLDER_AVATAR)
  const isKycVerified = computed(() => kycStatus.value === 'verified')
  const canBid = computed(() => isAuthenticated.value && user.value?.status !== 'banned' && user.value?.status !== 'suspended')
  const canPayout = computed(() => isKycVerified.value)
  const defaultCard = computed(() => savedCards.value.find(c => c.isDefault))
  const defaultPayoutMethod = computed(() => payoutMethods.value.find(p => p.isDefault))
  const activeBidsCount = computed(() => activeBids.value.length)
  const isAdmin = computed(() => {
    const r = user.value?.role
    return r === 'admin' || r === 'moderator'
  })
  const isSeller = computed(() => {
    return user.value?.role === 'seller' || user.value?.isSeller === true || isAdmin.value
  })
  const formattedBalance = computed(() => {
    const b: any = user.value?.balance
    const currency = t('common.currency')
    if (!b) return `0 ${currency}`
    if (typeof b === 'object') {
      if (b.amount !== undefined) return `${Number(b.amount).toLocaleString()} ${currency}`
      if (b.formatted) return b.formatted
    }
    if (typeof b === 'number') return `${b.toLocaleString()} ${currency}`
    return `${b} ${currency}`
  })

  // Who the account data in this store belongs to. Kept apart from `user`,
  // which an expired session (401) empties without clearing the rest.
  let lastUserId: string | null = null
  function applyUser(next: User) {
    // Signing in as someone else must not inherit the previous account's
    // documents, purchases or balance (they were merged into, not replaced) —
    // also after an expired session emptied `user`. The first user of a page
    // load has nothing to inherit, and clearing then would wipe what a page
    // had already loaded before the profile request came back.
    if (lastUserId !== null && lastUserId !== next.id) clearAccountData()
    lastUserId = next.id
    user.value = next
    isAuthenticated.value = true
    kycStatus.value = (next.kycStatus as KycStatus) || 'not_started'
    localStorage.setItem('user', JSON.stringify(next))
    if (next.kycDocuments) {
      kycDocuments.value = { ...kycDocuments.value, ...next.kycDocuments }
    }
    if (next.payoutMethods && next.payoutMethods.length > 0) {
      payoutMethods.value = next.payoutMethods as any
    }
  }

  // Actions
  async function fetchUser() {
    isLoading.value = true
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        user.value = null
        isAuthenticated.value = false
        return
      }

      const res = await authService.getMe()
      if (res?.user || res?.data) {
        applyUser((res.user || res.data) as User)
        return
      }

      // Token present but backend returned nothing usable → treat as signed out.
      user.value = null
      isAuthenticated.value = false
      localStorage.removeItem('token')
    } catch (err: any) {
      // Invalid/expired token or unreachable backend → do NOT fabricate a user.
      user.value = null
      isAuthenticated.value = false
    } finally {
      isLoading.value = false
    }
  }

  // Returns the signed-in user, or — when the account has two-step login on —
  // the challenge the code screen needs; then verifyLoginCode() finishes it.
  async function login(credentials: { email: string; password?: string }): Promise<
    { twoFactor: null; user: User } | { twoFactor: TwoFactorChallenge; user: null }
  > {
    isLoading.value = true
    error.value = null
    try {
      const res = await authService.login(credentials)
      if (res?.requiresTwoFactor && res.challengeId && res.channel) {
        return {
          twoFactor: {
            challengeId: res.challengeId,
            channel: res.channel,
            destination: res.destination || '',
            channels: res.channels || [],
            resendAfter: res.resendAfter ?? 30,
          },
          user: null,
        }
      }
      if (res?.token && (res?.user || res?.data)) {
        localStorage.removeItem('logged_out')
        localStorage.setItem('token', res.token)
        applyUser((res.user || res.data) as User)
        return { twoFactor: null, user: user.value! }
      }
      throw new Error('Login failed: empty response from server')
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function verifyLoginCode(challengeId: string, code: string) {
    const res = await authService.verifyLoginCode(challengeId, code)
    if (res?.token && (res?.user || res?.data)) {
      localStorage.removeItem('logged_out')
      localStorage.setItem('token', res.token)
      applyUser((res.user || res.data) as User)
      return user.value!
    }
    throw new Error('Login failed: empty response from server')
  }

  async function register(data: { fullName: string; email: string; phone: string; password?: string; city?: string }) {
    isLoading.value = true
    error.value = null
    try {
      const res = await authService.register(data)
      if (res?.token && (res?.user || res?.data)) {
        localStorage.removeItem('logged_out')
        localStorage.setItem('token', res.token)
        applyUser((res.user || res.data) as User)
        return user.value!
      }
      throw new Error('Registration failed: empty response from server')
    } catch (err: any) {
      error.value = err.message
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function fetchKycDetails() {
    try {
      const res = await userService.getKyc()
      if (res?.data) {
        kycStatus.value = res.data.status
      }
    } catch (err) {
      console.warn('Failed to fetch KYC details:', err)
    }
  }

  async function fetchPaymentMethods() {
    // Fetches payment cards
  }

  async function fetchPayoutMethods() {
    try {
      const res = await userService.getPayoutMethods()
      if (res?.data) {
        payoutMethods.value = res.data as any
      }
    } catch (err) {
      console.warn('Failed to fetch payout methods:', err)
    }
  }

  async function fetchBidHistory() {
    try {
      const res = await userService.getBids()
      if (res?.data) {
        activeBids.value = res.data
      }
    } catch (err) {
      console.warn('Failed to fetch bid history:', err)
    }
  }

  async function updateKycStatus(status: KycStatus) {
    kycStatus.value = status
    if (user.value) user.value.kycStatus = status
  }

  async function uploadKycDocument(type: string, file: File) {
    const res = await userService.uploadKycDocument(file, type)
    if (res?.url) {
      kycDocuments.value[type as keyof typeof kycDocuments.value] = res.url
      return { url: res.url }
    }
    throw new Error('Upload failed: no URL returned')
  }

  // A selfie that came out of the live head-turn check (see KycCapture).
  function setKycSelfie(url: string) {
    kycDocuments.value.selfie = url
  }

  async function submitKycForReview(options: { faceConsent?: boolean } = {}) {
    const res = await userService.submitKyc({
      idFrontUrl: kycDocuments.value.idFront,
      idBackUrl: kycDocuments.value.idBack,
      selfieUrl: kycDocuments.value.selfie,
      proofOfAddressUrl: kycDocuments.value.proofOfAddress,
      faceConsent: options.faceConsent === true,
    })
    if (res?.data) {
      kycStatus.value = res.data.status
      if (user.value) user.value.kycStatus = res.data.status
      return { status: res.data.status }
    }
    throw new Error('KYC submit failed: empty response from server')
  }

  // Alias for compatibility with DashboardPage.vue
  async function submitKyc(options: { faceConsent?: boolean } = {}) {
    return submitKycForReview(options)
  }

  async function addPaymentMethod(gateway: PaymentGateway, token: string, setDefault = false) {
    savedCards.value.push({
      id: `card-${Date.now()}`,
      brand: 'Visa',
      last4: token.slice(-4) || '4242',
      expiryMonth: 12,
      expiryYear: 2028,
      gateway,
      isDefault: setDefault,
    })
  }

  async function removePaymentMethod(id: string) {
    savedCards.value = savedCards.value.filter(c => c.id !== id)
  }

  async function addPayoutMethod(data: {
    bankCode: string
    accountNumber: string
    accountHolderName: string
    inn: string
  }) {
    const res = await userService.addPayoutMethod(data)
    if (res?.data) {
      const method = {
        id: res.data.id,
        bankCode: res.data.bankCode,
        bankName: res.data.bankName,
        accountNumber: res.data.accountNumber,
        accountHolderName: (res.data as any).accountHolderName || data.accountHolderName,
        isVerified: res.data.isVerified,
        isDefault: res.data.isDefault,
      }
      payoutMethods.value.push(method)
      return method
    }
    throw new Error('Add payout method failed: empty response from server')
  }

  async function verifyInn(inn: string) {
    return { isValid: inn.length === 14 && /^\d{14}$/.test(inn) }
  }

  async function requestPayout(amount: number, payoutMethodId: string) {
    const res = await userService.requestPayout(amount, payoutMethodId)
    if (res?.data) {
      payoutHistory.value.unshift(res.data)
      return res.data
    }
    throw new Error('Payout request failed: empty response from server')
  }

  async function fetchPayoutHistory(page = 1) {
    try {
      const res = await userService.getPayouts(page)
      if (res?.data) {
        payoutHistory.value = page === 1 ? res.data : [...payoutHistory.value, ...res.data]
      }
      if (res?.meta) {
        payoutHistoryMeta.value = res.meta
      }
      if (res?.balance) {
        payoutBalance.value = res.balance
      }
    } catch (err) {
      console.warn('Failed to fetch payout history:', err)
    }
  }

  async function fetchPurchases() {
    try {
      const [regular, groupBuy] = await Promise.all([
        userService.getPurchases(),
        userService.getGroupBuyPurchases(),
      ])
      const list = [...(regular?.data || []), ...(groupBuy?.data || [])]
      if (regular?.data || groupBuy?.data) purchases.value = list
    } catch (err) {
      console.warn('Failed to fetch purchases:', err)
    }
  }

  // A regular purchase is one-per-auction for a given buyer, so auction.id
  // identifies it. A group-buy purchase shares its auction.id with every
  // other participant's, so it's identified by its own payment.id instead.
  function replacePurchase(updated: Purchase | null) {
    if (!updated) return
    const idx = updated.kind === 'groupBuy'
      ? purchases.value.findIndex(p => p.kind === 'groupBuy' && p.payment?.id === updated.payment?.id)
      : purchases.value.findIndex(p => p.kind !== 'groupBuy' && p.auction.id === updated.auction.id)
    if (idx === -1) purchases.value.unshift(updated)
    else purchases.value.splice(idx, 1, updated)
  }

  // Both throw on failure so the card can show the server's real message —
  // no local "success" is ever faked for a money step.
  async function claimPayment(auctionId: string, body: { bankId?: string; reference: string; note?: string }) {
    const res = await userService.claimPayment(auctionId, body)
    if (!res?.success || !res.data) throw new Error('Payment claim failed: empty response from server')
    replacePurchase(res.data)
    return res.data
  }

  async function confirmDelivery(auctionId: string) {
    const res = await userService.confirmDelivery(auctionId)
    if (!res?.success) throw new Error('Delivery confirmation failed: empty response from server')
    replacePurchase(res.data)
    return res.data
  }

  async function claimGroupBuyPayment(paymentId: string, body: { bankId?: string; reference: string; note?: string }) {
    const res = await userService.claimGroupBuyPayment(paymentId, body)
    if (!res?.success || !res.data) throw new Error('Payment claim failed: empty response from server')
    replacePurchase(res.data)
    return res.data
  }

  async function confirmGroupBuyDelivery(paymentId: string) {
    const res = await userService.confirmGroupBuyDelivery(paymentId)
    if (!res?.success) throw new Error('Delivery confirmation failed: empty response from server')
    replacePurchase(res.data)
    return res.data
  }

  async function updateProfile(data: Partial<User>) {
    try {
      const res = await userService.updateProfile(data)
      if (res?.data || (res as any)?.user) {
        const updated = ((res.data || (res as any)?.user)) as User
        applyUser({ ...(user.value || {}), ...updated } as User)
        return
      }
    } catch (err) {
      console.warn('Update profile API failed, using fallback')
    }

    if (user.value) {
      applyUser({ ...user.value, ...data, updatedAt: new Date().toISOString() } as User)
    }
  }

  async function changePassword(currentPassword: string, newPassword: string) {
    // Used to fall back to { success: true } whenever the server answered
    // without success — a failed change was reported as done.
    let res: any
    try {
      res = await userService.changePassword(currentPassword, newPassword)
    } catch (err: any) {
      throw new Error(err?.data?.error || err?.response?.data?.error || err.message)
    }
    if (!res?.success) throw new Error(res?.error || res?.message || 'Password change failed')
    // Every older session is signed out by the server; keep this one alive.
    if (res.token) localStorage.setItem('token', res.token)
    return res
  }


  function clearAccountData() {
    kycDocuments.value = {}
    purchases.value = []
    payoutBalance.value = null
    payoutHistory.value = []
    wonAuctions.value = []
    useSavedSearches().reset()
  }

  function logout() {
    authService.logout().catch(() => {})
    clearAccountData()
    user.value = null
    isAuthenticated.value = false
    kycStatus.value = 'not_started'
    savedCards.value = []
    payoutMethods.value = []
    activeBids.value = []
    localStorage.removeItem('logged_out')
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  }

  return {
    // State
    user,
    isAuthenticated,
    isLoading,
    error,
    kycStatus,
    kycDocuments,
    savedCards,
    purchases,
    payoutBalance,
    payoutMethods,
    payoutHistory,
    payoutHistoryMeta,
    activeBids,
    wonAuctions,
    
    // Getters
    fullName,
    avatar,
    isKycVerified,
    canBid,
    canPayout,
    defaultCard,
    defaultPayoutMethod,
    activeBidsCount,
    isAdmin,
    isSeller,
    formattedBalance,
    
    // Actions
    fetchUser,
    login,
    verifyLoginCode,
    register,
    fetchKycDetails,
    fetchPaymentMethods,
    fetchPayoutMethods,
    fetchBidHistory,
    updateKycStatus,
    uploadKycDocument,
    setKycSelfie,
    submitKycForReview,
    submitKyc, // alias
    addPaymentMethod,
    removePaymentMethod,
    addPayoutMethod,
    verifyInn,
    requestPayout,
    fetchPayoutHistory,
    fetchPurchases,
    claimPayment,
    confirmDelivery,
    claimGroupBuyPayment,
    confirmGroupBuyDelivery,
    updateProfile,
    changePassword,
    logout,
  }
})